"use client"

import * as React from "react"
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import { CircleIcon } from "lucide-react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@workspace/ui/lib/utils"

function RadioGroup({
  className,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Root>) {
  return (
    <RadioGroupPrimitive.Root
      data-slot="radio-group"
      className={cn("grid gap-3", className)}
      {...props}
    />
  )
}

const radioGroupItemVariants = cva(
  "aspect-square shrink-0 rounded-full border shadow-xs transition-[background-color,border-color,box-shadow] outline-none text-primary bg-background/50 border-gray-200 dark:border-gray-700 dark:bg-background/30 ring-1 ring-white/10 dark:ring-white/5 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] hover:bg-background/70 dark:hover:bg-background/40 hover:border-gray-300 dark:hover:border-gray-600 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      size: {
        sm: "size-3",
        default: "size-4",
        lg: "size-5"
      }
    },
    defaultVariants: {
      size: "default"
    }
  }
)

const radioGroupIndicatorVariants = cva(
  "fill-primary absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
  {
    variants: {
      size: {
        sm: "size-1.5",
        default: "size-2",
        lg: "size-2.5"
      }
    },
    defaultVariants: {
      size: "default"
    }
  }
)

export interface RadioGroupItemProps
  extends React.ComponentProps<typeof RadioGroupPrimitive.Item>,
    VariantProps<typeof radioGroupItemVariants> {}

function RadioGroupItem({
  className,
  size,
  ...props
}: RadioGroupItemProps) {
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-item"
      className={cn(radioGroupItemVariants({ size }), className)}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="relative flex items-center justify-center"
      >
        <CircleIcon className={cn(radioGroupIndicatorVariants({ size }))} />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  )
}

export { RadioGroup, RadioGroupItem }
